import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Eye, Plus, Check } from 'lucide-react';
import { img, labelize, cx } from './ui';
import { useInquiryCart } from '../../../context/InquiryCartContext';
import { useWishlist } from '../../../context/WishlistContext';

/* Single product tile. Photography leads; controls appear on hover (and are
 * always reachable by keyboard). No prices — the CTA is enquiry, not purchase. */
export default function ProductCard({ product, onQuickView }) {
  const { addItem, openCart, isInCart } = useInquiryCart();
  const wishlist = useWishlist();
  if (!product) return null;

  const inCart = isInCart(product.id);
  const saved = wishlist.has(product.id);

  const requestQuote = () => {
    addItem(product, 1);
    openCart();
  };

  return (
    <article className="group relative flex flex-col">
      <div className="relative overflow-hidden bg-[#ece0cd] aspect-[4/5]">
        <Link to={`/products/${product.slug}`} aria-label={product.name}>
          <img
            src={img(product.image_url)}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
          />
          <span className="absolute inset-0 bg-[#160f0a00] group-hover:bg-[#160f0a1a] transition-colors duration-500" />
        </Link>

        {/* wishlist */}
        <button
          type="button"
          onClick={() => wishlist.toggle(product.id)}
          aria-pressed={saved}
          aria-label={saved ? 'Remove from wishlist' : 'Save to wishlist'}
          className={cx(
            'absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full',
            'backdrop-blur bg-[#f4ece0d9] hover:bg-[#f4ece0] transition-colors',
            saved ? 'text-[#b5622f]' : 'text-[#241811]'
          )}
        >
          <Heart className="w-[17px] h-[17px]" fill={saved ? 'currentColor' : 'none'} />
        </button>

        {/* hover action bar */}
        <div className="absolute inset-x-0 bottom-0 p-3 flex gap-2 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 focus-within:translate-y-0 focus-within:opacity-100 transition-all duration-300">
          {onQuickView && (
            <button
              type="button"
              onClick={() => onQuickView(product)}
              className="flex-1 flex items-center justify-center gap-2 bg-[#f4ece0f2] text-[#241811] text-[11px] uppercase tracking-[0.14em] py-2.5 hover:bg-white transition-colors"
            >
              <Eye className="w-4 h-4" /> Quick view
            </button>
          )}
          <button
            type="button"
            onClick={() => addItem(product, 1)}
            aria-label="Add to inquiry"
            className="w-11 flex items-center justify-center bg-[#241811] text-[#f4ece0] hover:bg-[#3d2b1f] transition-colors"
          >
            {inCart ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="pt-4">
        {product.category && (
          <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#b5622f] mb-1.5">
            {labelize(product.category)}
          </div>
        )}
        <h3 className="font-display text-[17px] leading-snug">
          <Link to={`/products/${product.slug}`} className="hover:text-[#b5622f] transition-colors">
            {product.name}
          </Link>
        </h3>
        {product.description && (
          <p className="text-[13px] text-[#4a3d32] mt-1.5 line-clamp-2">{product.description}</p>
        )}
        <button
          type="button"
          onClick={requestQuote}
          className="mt-3 text-[11px] uppercase tracking-[0.16em] text-[#241811] border-b border-[#241811]/30 pb-0.5 hover:border-[#b5622f] hover:text-[#b5622f] transition-colors self-start"
        >
          Request quote
        </button>
      </div>
    </article>
  );
}
